import { logger } from './logger';
import { queryClient } from './query-client';

const API_BASE = '/client/api';

interface ApiOptions extends RequestInit {
  params?: Record<string, string>;
}

// CSRF token cache
let csrfTokenPromise: Promise<string | null> | null = null;

/** Cache-bust counter for CSRF token refresh */
let csrfTokenVersion = 0;

/**
 * Fetch the CSRF token from the server.
 * The response sets an httpOnly cookie; we also get the token value to send as a header.
 */
async function fetchCsrfToken(): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE}/csrf-token`, {
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.csrfToken || null;
  } catch (err) {
    logger.warn('[API] Failed to fetch CSRF token:', err instanceof Error ? err.message : err);
    return null;
  }
}

/**
 * Get the current CSRF token, fetching a fresh one if needed.
 */
export async function getCsrfToken(): Promise<string | null> {
  if (!csrfTokenPromise) {
    csrfTokenPromise = fetchCsrfToken();
  }
  return csrfTokenPromise;
}

/**
 * Invalidate the cached CSRF token so the next request fetches a fresh one.
 */
export function refreshCsrfToken(): void {
  csrfTokenPromise = null;
  csrfTokenVersion++;
}

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem('auth-storage');
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);
    return parsed.state?.token || null;
  } catch (err) {
    logger.warn('[API] Failed to parse auth-storage for token:', err instanceof Error ? err.message : err);
    return null;
  }
}

// ── Session-expiry signalling ─────────────────────────────────────────────
// Dispatched when the refresh token can no longer be exchanged for a new
// access token (expired or revoked server-side). The auth store listens and
// forces a logout, so the user lands on the login page instead of a broken
// dashboard where every request 401s with no recovery.
export const SESSION_EXPIRED_EVENT = 'auth:session-expired';

function signalSessionExpired(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }
}

// Single in-flight refresh — when a burst of requests 401s at once (e.g. on
// navigation), they all await ONE /auth/refresh call instead of hammering the
// endpoint once per failing request (which previously produced a storm of
// refresh 401s in the console).
let refreshPromise: Promise<string | null> | null = null;

export function refreshToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = doRefreshToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function doRefreshToken(): Promise<string | null> {
  const stored = localStorage.getItem('auth-storage');
  if (!stored) return null;

  try {
    const parsed = JSON.parse(stored);
    const refreshTokenValue = parsed.state?.refreshToken;
    if (!refreshTokenValue) {
      signalSessionExpired();
      return null;
    }

    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: refreshTokenValue }),
    });

    if (res.status === 401) {
      // Refresh token rejected — the session is genuinely dead (expired or
      // revoked server-side). Signal so the auth store forces a logout.
      signalSessionExpired();
      return null;
    }

    // Any other non-OK status (429 rate-limited, 5xx, network) is transient —
    // do NOT nuke the session for those; the original error will surface and
    // the next attempt can retry.
    if (!res.ok) return null;

    const data = await res.json();
    if (data.success) {
      // Update stored token
      parsed.state.token = data.data.token;
      parsed.state.refreshToken = data.data.refreshToken;
      localStorage.setItem('auth-storage', JSON.stringify(parsed));
      return data.data.token;
    }
    return null;
  } catch (err) {
    logger.warn('[API] Token refresh failed:', err instanceof Error ? err.message : err);
    return null;
  }
}

export async function api<T = any>(
  endpoint: string,
  options: ApiOptions = {}
): Promise<{ success: boolean; data?: T; error?: string; message?: string }> {
  const { params, ...fetchOptions } = options;
  const token = getToken();

  let url = `${API_BASE}${endpoint}`;

  if (params) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Don't set Content-Type for FormData (browser sets it with boundary)
  const isFormData = fetchOptions.body instanceof FormData;
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Add CSRF token header for state-changing methods
  const method = (fetchOptions.method || 'GET').toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    try {
      const csrfToken = await getCsrfToken();
      if (csrfToken) {
        headers['x-csrf-token'] = csrfToken;
      }
    } catch (err) {
      logger.warn('[API] CSRF token fetch failed, request may be rejected:', err instanceof Error ? err.message : err);
    }
  }

  let res = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  // If 401, try refreshing token once
  if (res.status === 401 && token) {
    const newToken = await refreshToken();
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      res = await fetch(url, {
        ...fetchOptions,
        headers,
      });
    }
  }

  const data = await res.json();

  if (!res.ok) {
    const error = new Error(data.error || 'An error occurred');
    // Attach rate limit info from headers (sent by express-rate-limit with standardHeaders: true)
    const remaining = res.headers.get('RateLimit-Remaining');
    const reset = res.headers.get('RateLimit-Reset');
    const limit = res.headers.get('RateLimit-Limit');
    if (remaining !== null) {
      (error as any).rateLimitRemaining = parseInt(remaining, 10);
      (error as any).rateLimitReset = reset ? parseInt(reset, 10) : undefined;
      (error as any).rateLimitLimit = limit ? parseInt(limit, 10) : undefined;
    }
    throw error;
  }

  // After a successful state-changing request, refresh the query families that
  // display this entity or its aggregates (see ENTITY_INVALIDATIONS above).
  // This keeps cross-entity pages (client detail, project detail, dashboard,
  // reports, nav badges) up to date without manual browser refreshes.
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    invalidateFor(endpoint);
  }

  return data;
}

// ── Cross-entity cache invalidation ────────────────────────────────────────
// React Query caches each query under its own key, and mutations only
// invalidate their own entity's keys. That leaves related pages that embed the
// same data stale until a hard refresh — e.g. deleting an invoice from the
// invoices page doesn't refresh the client detail page (which shows that
// client's invoices/counts) or the dashboard widgets.
//
// This map fixes that centrally: after ANY successful mutation we invalidate
// the entity's own query families PLUS every family that displays aggregates
// or cross-references of it. Keys are query-key prefixes; invalidateQueries
// fuzzy-matches them (['clients'] also matches ['clients', id], etc.).
const ENTITY_INVALIDATIONS: Record<string, string[]> = {
  clients: ['clients', 'client', 'users', 'report-projects'],
  invoices: [
    'invoices', 'invoice', 'clients', 'client', 'projects', 'project',
    'report-time', 'report-financial', 'report-projects', 'invoice-stats', 'nav-counts',
  ],
  projects: [
    'projects', 'project', 'clients', 'client', 'site-monitors',
    'report-time', 'report-financial', 'report-projects', 'invoice-stats', 'nav-counts',
  ],
  quotes: [
    'quotes', 'quote', 'clients', 'client', 'projects', 'project', 'leads',
    'invoices', 'invoice',
    'report-time', 'report-financial', 'report-projects', 'invoice-stats', 'nav-counts',
  ],
  leads: ['leads', 'quotes', 'quote', 'clients', 'client', 'users', 'report-time', 'report-projects'],
  tasks: ['tasks', 'task', 'projects', 'project', 'report-time'],
  tickets: ['tickets', 'ticket', 'clients', 'client', 'projects', 'project', 'report-time', 'nav-counts'],
  files: ['files', 'projects', 'project', 'tasks', 'task'],
  users: ['users', 'user', 'clients', 'client'],
  notifications: ['notifications', 'nav-counts'],
  'notification-preferences': ['notification-preferences'],
  'site-monitors': ['site-monitors', 'projects', 'project'],
  'time-entries': ['time-entries', 'projects', 'project', 'report-time'],
  invitations: [
    'invitations', 'quotes', 'quote', 'users', 'clients', 'client',
    'projects', 'project', 'invoices', 'invoice', 'invoice-stats', 'nav-counts',
  ],
  'email-templates': ['email-templates', 'templates', 'template'],
  templates: ['templates', 'template', 'projects', 'project'],
  'portal-settings': ['portal-settings', 'terms'],
  settings: ['portal-settings', 'terms', 'email-templates', 'templates'],
  'broadcast-banners': ['broadcast-banners'],
  subscriptions: [
    'subscriptions', 'invoices', 'invoice', 'site-monitors', 'nav-counts',
    'report-financial', 'invoice-stats',
  ],
  'service-templates': ['service-templates', 'templates'],
  knowledge: ['knowledge'],
};

/**
 * Resolve which query families a mutation on `endpoint` should invalidate.
 * The entity is the first URL segment (e.g. /invoices/:id → "invoices").
 * Unknown entities (e.g. auth routes) return an empty list — no invalidation.
 */
export function getInvalidationTargets(endpoint: string): string[] {
  const path = endpoint.split('?')[0];
  const entity = path.split('/').filter(Boolean)[0] || '';
  return ENTITY_INVALIDATIONS[entity] || [];
}

/** Invalidate all query families affected by a mutation on `endpoint`. */
function invalidateFor(endpoint: string): void {
  for (const prefix of getInvalidationTargets(endpoint)) {
    queryClient.invalidateQueries({ queryKey: [prefix] });
  }
}

// Convenience methods
export const apiClient = {
  get: <T = any>(endpoint: string, params?: Record<string, string>) =>
    api<T>(endpoint, { method: 'GET', params }),

  post: <T = any>(endpoint: string, body?: any) =>
    api<T>(endpoint, { method: 'POST', body: JSON.stringify(body) }),

  put: <T = any>(endpoint: string, body?: any) =>
    api<T>(endpoint, { method: 'PUT', body: JSON.stringify(body) }),

  patch: <T = any>(endpoint: string, body?: any) =>
    api<T>(endpoint, { method: 'PATCH', body: JSON.stringify(body) }),

  delete: <T = any>(endpoint: string) =>
    api<T>(endpoint, { method: 'DELETE' }),

  upload: <T = any>(endpoint: string, formData: FormData) =>
    api<T>(endpoint, { method: 'POST', body: formData }),
};
